import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Edit2, SkipForward } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import { useAppSelector } from '../../store/hooks';
import Button from '../UI/Button';
import Modal from '../UI/Modal';
import Input from '../UI/Input';
import { userManagementService } from '../../services/userManagementService';

interface HistoricalEventImportErrorReviewProps {
  isOpen: boolean;
  onClose: () => void;
  errors: Array<{
    sheet: string;
    row: number;
    error: string;
    guest_name: string;
    data: any;
  }>;
  importBatchId: string;
}

const HistoricalEventImportErrorReview: React.FC<HistoricalEventImportErrorReviewProps> = ({
  isOpen,
  onClose,
  errors,
  importBatchId
}) => {
  const { showNotification } = useNotification();
  const { theme } = useAppSelector((state) => state.ui);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<any>(null);
  const [skippedRows, setSkippedRows] = useState<Set<number>>(new Set());
  const [fixedRows, setFixedRows] = useState<Set<number>>(new Set());
  const [successfulRows, setSuccessfulRows] = useState<Set<number>>(new Set());
  const [retryingRow, setRetryingRow] = useState<number | null>(null);
  const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());

  const handleEdit = (index: number) => {
    const error = errors[index];
    setEditingRow(index);
    // Ensure all required fields are included
    setEditedData({ 
      ...error.data,
      event_name: error.data?.event_name || error.data?.sheet || '',
      imported_from_file: error.data?.imported_from_file || 'unknown.xlsx'
    });
  };

  const handleSaveEdit = async (index: number) => {
    // Validate edited data
    if (!editedData.name || !editedData.name.trim()) {
      showNotification('Name is required', 'error');
      return;
    }
    if (!editedData.phone || !editedData.phone.trim()) {
      showNotification('Phone number is required', 'error');
      return;
    }

    setRetryingRow(index);
    try {
      // Retry importing the row with fixed data
      const result = await userManagementService.retryHistoricalEventRow(
        editedData,
        importBatchId,
        editedData.imported_from_file || 'unknown.xlsx'
      );

      if (result.success) {
        // Mark as successful
        const newSuccessfulRows = new Set(successfulRows);
        newSuccessfulRows.add(index);
        setSuccessfulRows(newSuccessfulRows);
        
        // Remove from fixed rows if it was there
        const newFixedRows = new Set(fixedRows);
        newFixedRows.delete(index);
        setFixedRows(newFixedRows);
        
        // Clear any previous error for this row
        const newRowErrors = new Map(rowErrors);
        newRowErrors.delete(index);
        setRowErrors(newRowErrors);
        
        setEditingRow(null);
        showNotification(result.message || 'Row imported successfully!', 'success');
      } else {
        // Show new error
        const newRowErrors = new Map(rowErrors);
        newRowErrors.set(index, result.error?.message || result.error?.details || 'Import failed');
        setRowErrors(newRowErrors);
        
        // Remove from successful if it was there
        const newSuccessfulRows = new Set(successfulRows);
        newSuccessfulRows.delete(index);
        setSuccessfulRows(newSuccessfulRows);
        
        showNotification(result.error?.message || 'Failed to import row', 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to retry import';
      const newRowErrors = new Map(rowErrors);
      newRowErrors.set(index, errorMessage);
      setRowErrors(newRowErrors);
      showNotification(errorMessage, 'error');
    } finally {
      setRetryingRow(null);
    }
  };

  const handleSkip = (index: number) => {
    const newSkippedRows = new Set(skippedRows);
    newSkippedRows.add(index);
    setSkippedRows(newSkippedRows);
    showNotification('Row marked as skipped', 'info');
  };

  const handleSkipAll = () => {
    const allIndices = errors.map((_, index) => index);
    setSkippedRows(new Set(allIndices));
    showNotification('All rows marked as skipped', 'info');
  };

  const getRowStatus = (index: number) => {
    if (skippedRows.has(index)) return 'skipped';
    if (successfulRows.has(index)) return 'success';
    if (fixedRows.has(index)) return 'fixed';
    return 'pending';
  };

  const pendingErrors = errors.filter((_, index) => getRowStatus(index) === 'pending');
  const fixedCount = fixedRows.size;
  const skippedCount = skippedRows.size;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review Import Errors"
      size="3xl"
    >
      <div className="space-y-4">
        {/* Summary */}
        <div className={`border rounded-lg p-4 ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Errors</p>
              <p className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{errors.length}</p>
            </div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Fixed</p>
              <p className="text-2xl font-bold text-green-600">{fixedCount}</p>
            </div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Skipped</p>
              <p className="text-2xl font-bold text-yellow-600">{skippedCount}</p>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {pendingErrors.length > 0 && (
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSkipAll}
            >
              Skip All
            </Button>
          </div>
        )}

        {/* Errors Table */}
        <div className={`border rounded-lg overflow-hidden ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '50vh' }}>
            <table className={`w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`} style={{ minWidth: '900px', width: '100%' }}>
              <thead className={`sticky top-0 z-10 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
              }`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[100px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Sheet
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[60px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Row
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[150px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Guest Name
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[120px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Phone Number
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[200px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Error
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[100px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Status
                  </th>
                  <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider min-w-[120px] ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                theme === 'dark' 
                  ? 'bg-gray-900 divide-gray-700' 
                  : 'bg-white divide-gray-200'
              }`}>
                {errors.map((error, index) => {
                  const status = getRowStatus(index);
                  const isEditing = editingRow === index;

                  return (
                    <tr key={index} className={
                      status === 'skipped' 
                        ? theme === 'dark' 
                          ? 'bg-gray-800 opacity-60' 
                          : 'bg-gray-50 opacity-60'
                        : theme === 'dark'
                        ? 'hover:bg-gray-800'
                        : 'hover:bg-gray-50'
                    }>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {error.sheet}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {error.row}
                      </td>
                      <td className={`px-4 py-3 text-sm min-w-[150px] ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {isEditing ? (
                          <Input
                            value={editedData?.name || ''}
                            onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                            className="w-full"
                            placeholder="Guest name"
                          />
                        ) : (
                          <div className="break-words">{error.guest_name || 'N/A'}</div>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm min-w-[120px] ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {isEditing ? (
                          <Input
                            value={editedData?.phone || ''}
                            onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
                            className="w-full"
                            placeholder="Phone number (e.g., +962771234567)"
                          />
                        ) : (
                          <div className="break-words">{error.data?.phone || 'N/A'}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm min-w-[200px]">
                        {rowErrors.has(index) ? (
                          <div className="text-red-500 break-words">{rowErrors.get(index)}</div>
                        ) : (
                          <div className={theme === 'dark' ? 'text-red-400 break-words' : 'text-red-600 break-words'}>{error.error}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {status === 'success' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Added
                          </span>
                        )}
                        {status === 'fixed' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Fixed
                          </span>
                        )}
                        {status === 'skipped' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <SkipForward className="h-3 w-3 mr-1" />
                            Skipped
                          </span>
                        )}
                        {status === 'pending' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        {isEditing ? (
                          <div className="flex justify-end space-x-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingRow(null);
                                setEditedData(null);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleSaveEdit(index)}
                              disabled={retryingRow === index}
                              loading={retryingRow === index}
                            >
                              {retryingRow === index ? 'Importing...' : 'Save & Retry'}
                            </Button>
                          </div>
                        ) : status === 'pending' || status === 'fixed' ? (
                          <div className="flex justify-end space-x-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(index)}
                              title="Edit and fix data"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleSkip(index)}
                              title="Skip this row"
                            >
                              <SkipForward className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Message */}
        <div className={`border rounded-lg p-4 ${
          theme === 'dark' 
            ? 'bg-blue-900/20 border-blue-700' 
            : 'bg-blue-50 border-blue-200'
        }`}>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
          }`}>
            <strong>Note:</strong> Fixed rows will need to be re-imported manually. Skipped rows will be ignored.
            You can export the fixed data and import it again, or manually add the guests.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HistoricalEventImportErrorReview;

